<template>
  <div id="web-editor">
    <div class="edit-draft">
      <header class="editor-header">
        <input
          placeholder="输入文章标题..."
          spellcheck="false"
          maxlength="80"
          class="title-input"
          v-model="form.title" @input="AutoCreateDrafts" />
        <div class="right-box">
          <div class="topic-icon"></div>
          <div
            :title="drafts.resultupdatetime"
            class="status-text with-padding">
            {{drafts.resulttext}}
          </div>
          <button class="btn-drafts" @click="closeWindow">草稿箱</button>
          <div class="with-padding">
            <button
              class="publish-btn"
              @click="publishBtn">
              发布
            </button>
          </div>
        </div>
      </header>
    </div>
    <div class="md-editor">
      <Editor
        :value="editorstate.value"
        :locale="editorstate.zh"
        :upload-images="handleUploadFile"
        :plugins="editorstate.plugins"
        @change="handleChange" />
    </div>
  </div>
  <el-dialog
    v-model="editorstate.dialogFormVisible"
    title="发布文章"
    width="580">
    <div class="plusdivider"></div>
    <el-form
      ref="ruleFormRef"
      :model="form"
      :rules="rules">
      <el-form-item
        label="分类"
        :label-width="formLabelWidth"
        prop="category_id">
        <el-radio-group v-model="form.category_id">
          <el-radio
            :key="category.category_id"
            :value="category.category_id"
            v-for="category in state.categorylistData"
            border
            >{{ category.category_name }}
          </el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item
        label="文章封面"
        :label-width="formLabelWidth"
        prop="cover_image">
        <div
          class="el-upload-list el-upload-list--picture-card"
          v-if="imageInfo.imageVisible">
          <img
            class="el-upload-list__item-thumbnail"
            :src="imageInfo.imageUrl"
            alt="" />
          <span class="el-upload-list__item-actions">
            <span
              class="el-upload-list__item-delete"
              @click="handleRemove()">
              <el-icon><Delete /></el-icon>
            </span>
          </span>
        </div>
        <el-upload
          v-if="!imageInfo.imageVisible"
          class="avatar-uploader"
          :action="uploadCoverData.uploadactionurl"
          :headers="UpLoadHeaders"
          method="POST"
          name="myIfile"
          :data="uploadCoverData"
          :show-file-list="false"
          :on-success="handleAvatarSuccess"
          :before-upload="beforeAvatarUpload">
          <el-icon class="avatar-uploader-icon"><Plus /> </el-icon>
          <div class="upload-text">上传封面</div>
        </el-upload>
        <div>建议尺寸：192*128px (封面仅展示在首页信息流中)</div>
      </el-form-item>
      <el-form-item
        label="编辑摘要"
        :label-width="formLabelWidth"
        prop="brief_content">
        <el-input
          v-model="form.brief_content"
          maxlength="100"
          style="width: 400px"
          rows="4"
          placeholder=""
          show-word-limit
          type="textarea" />
      </el-form-item>

      <el-form-item class="button-group-right">
        <el-button
          type="primary"
          @click="submitForm(ruleFormRef)">
          确定并发布
        </el-button>
        <el-button @click="editorstate.dialogFormVisible = false"
          >取消</el-button
        >
      </el-form-item>
    </el-form>
  </el-dialog>
</template>

<script setup lang="ts">
import { markRaw, reactive, ref, onMounted,computed } from "vue";
import type { FormInstance, FormRules, UploadProps } from "element-plus";
import { ElMessage } from "element-plus";
import { useRouter } from 'vue-router'
import axiosInstance from "@/utils/axiosInstance";
import FileStorageInstance from "@/utils/axiosFileStorage";
import { FileStorage_URL } from "@/interface/globalindex";
import { v4 as uuidv4 } from 'uuid';

import "bytemd/dist/index.css";
import { Editor } from "@bytemd/vue-next";
//支持GFM(自动链接文字、删除、表格、任务列表)
import gfm from "@bytemd/plugin-gfm";
// 突出显示代码块
import highlight from "@bytemd/plugin-highlight";
import "highlight.js/styles/default.css";

// 对齐方式
import alignPlugin from "bytemd-plugin-align";
// 主题
import theme from "bytemd-plugin-theme";
// 解析前言 格式化
import frontmatter from "@bytemd/plugin-frontmatter";
// 表情
import gemoji from "@bytemd/plugin-gemoji";

// 代码高亮
import highLightPlugin from "bytemd-plugin-highlight";
// 图片的缩放
import imagePlugin from "bytemd-plugin-image";
// 代码复制
// import copyCode from "bytemd-plugin-copy-code";
// 缩放图片 点击图片放大,再点击缩小
import mediumZoom from "@bytemd/plugin-medium-zoom";
// 图表
import mermaid from "@bytemd/plugin-mermaid";
import mermaidLocale from "@bytemd/plugin-mermaid/locales/zh_Hans.json";
// 数学公式插件
import math from "@bytemd/plugin-math";
import mathLocale from "@bytemd/plugin-math/locales/zh_Hans.json";
import "katex/dist/katex.css";

import zhHans from "bytemd/locales/zh_Hans.json";
import { useOAuthStore } from '@/stores/oauth'
const authStore = useOAuthStore()
const token = authStore.token
// 计算属性生成带 token 的 headers
const UpLoadHeaders = computed(() => ({
  Authorization: `Bearer ${token}` // 根据实际存储方式调整
}));

const router = useRouter()

const formLabelWidth = "83px";

//插件列表
const pluginsList = [
  gfm(),
  math({
    locale: mathLocale,
  }),
  alignPlugin(),
  highLightPlugin(),
  imagePlugin(),
  // copyCode(),
  mediumZoom(),
  mermaid({
    locale: mermaidLocale,
  }),
  highlight(),
  theme(),
  frontmatter(),
  gemoji()
];
//编辑器参数
const editorstate = reactive({
  value: "",
  plugins: markRaw(pluginsList),
  zh: zhHans,
  dialogFormVisible: ref(false),
  postID:''
});

//生成GUID
const generateGUID = () => uuidv4();

onMounted(() => {
  getCategory();
  editorstate.postID = generateGUID();
  uploadCoverData.articleId = editorstate.postID;
});

//分类
const state = reactive({
  categorylistData: [], // 数据列表
});

// 获取分类列表
const getCategory = () => {
  axiosInstance
    .get("/api/ArticleInfo/GetCategoryList", {
      params: {},
    })
    .then((res) => {
      var result = res.data;
      if (result.success) {
        state.categorylistData = result.data;
      }
    });
};

interface RuleForm {
  article_id: string;
  title: string;
  info_content: string;
  category_id: string;
  cover_image: string;
  brief_content: string;
  link_url: string;
  picurls: [];
}
const ruleFormRef = ref<FormInstance>();

const form = reactive<RuleForm>({
  article_id: "",
  title: "",
  info_content: "",
  category_id: "",
  cover_image: "",
  brief_content: "",
  picurls: [],
  link_url: "",
});

//事件 自动保存草稿箱
const handleChange = (v: string) => {
  editorstate.value = v;
  form.info_content = editorstate.value;
  AutoCreateDrafts();
};
//封面地址
const imageInfo = reactive({
  imageVisible: false,
  imageUrl: "",
});

//封面上传额外参数
const uploadCoverData = reactive({
  articleId: '' ,
  uploadactionurl: FileStorage_URL+'/api/ArticleInfo/UploadCoverImage'
});

//封面上传成功回调函数
const handleAvatarSuccess: UploadProps["onSuccess"] = (response) => {
  if (response.success) {
    imageInfo.imageUrl = response.data;
    imageInfo.imageVisible = true;
    form.cover_image = response.data;
  }
};

//移除封面
const handleRemove = () => {
  imageInfo.imageVisible = false;
  imageInfo.imageUrl = "";
  form.cover_image = "";
};

//封面格式大小限制
const beforeAvatarUpload: UploadProps["beforeUpload"] = (rawFile) => {
  if (rawFile.type !== "image/jpeg") {
    ElMessage.error("Avatar picture must be image format!");
    return false;
  } else if (rawFile.size / 1024 / 1024 > 15) {
    ElMessage.error("Avatar picture size can not exceed 15MB!");
    return false;
  }
  return true;
};

//编辑器上传图片
const handleUploadFile = async (files) => {
  let fileData = new FormData();
  //多图片上传
  for (let i = 0; i < files.length; i++) {
    fileData.append("files", files[i]);
  }
  fileData.append("articleId", editorstate.postID);
  await FileStorageInstance
    .post("api/ArticleInfo/UploadArticleImage", fileData, {
      headers: { "content-Type": "multipart/form-data" },
    })
    .then((res) => {
      var result = res.data;
      if (result.success) {
        form.picurls = result.data;
      }
    });
  return form.picurls;
};

//发布
const publishBtn = () => {
  if (form.title === "") {
    ElMessage.error("请输入文章标题");
    return;
  }
  if (form.info_content === "") {
    ElMessage.error("请输入文章内容");
    return;
  }
  editorstate.dialogFormVisible = true;
};
const rules = reactive<FormRules<RuleForm>>({
  category_id: [{ required: true, message: "请选择一个分类", trigger: "blur" }],
  brief_content: [
    { required: true, message: "摘要不满足最低50字的要求", trigger: "blur" },
    { min: 50, max: 100, message: "摘要不满足最低50字的要求", trigger: "blur" },
  ],
});

//发布文章
const submitForm = (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  formEl.validate((valid) => {
    if (valid) {
      let params = {
        article_id: editorstate.postID,
        title: form.title,
        info_content: form.info_content,
        category_id: form.category_id,
        brief_content: form.brief_content,
        link_url: form.link_url,
        cover_image: form.cover_image,
        drafts_id:drafts.drafts_id
      };

      axiosInstance
        .post("api/ArticleInfo/ArticleInfoSave", params)
        .then((res) => {
          var result = res.data;
          if (result.success) {
            router.push({ path: "/published"});
          }
        });
    } else {
      console.log("error submit!");
    }
  });
};

//草稿箱参数
const drafts = reactive({
  drafts_id: "",
  title: "",
  info_content: "",
  category_id: "",
  cover_image: "",
  brief_content: "",
  status: 1,
  resulttext:'文章将自动保存至草稿箱',
  resulttitle:'',
  resultupdatetime:''
});

//自动保存草稿箱
const AutoCreateDrafts = () => {

  let postdata = {
    drafts_id: drafts.drafts_id,
    title: form.title,
    info_content: form.info_content,
    category_id: form.category_id,
    brief_content: form.brief_content,
    link_url: form.link_url,
    cover_image: form.cover_image,
    status:drafts.status
  };
  drafts.resulttext = '保存中...';
  axiosInstance.post("api/ArticleDrafts/ArticleDraftsSave", postdata).then((res) => {
    var result = res.data;
    if (result.success) {
      drafts.drafts_id = result.data.drafts_id;	
      drafts.resultupdatetime = '最近保存于 '+result.data.updated_at;
      drafts.resulttext = '保存成功';
    }
  });
};

//点击草稿箱 关闭窗口
const closeWindow = () => {
    window.close();
}


</script>

<style scoped>
#web-editor {
  display: block;
  height: 100vh;
}

.editor-header {
  padding: 0 27px;
  height: 63px;
  background-color: #fff;
  border-bottom: 1px solid #ddd;
  z-index: 100;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  padding-right: 14px;
  cursor: pointer;
}

.title-input {
  -webkit-box-flex: 1;
  -ms-flex: 1 1 auto;
  flex: 1 1 auto;
  height: 100%;
  margin: 0;
  padding: 0;
  font-size: 24px;
  font-weight: 500;
  color: #1d2129;
  border: none;
  outline: none;
}

.right-box {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-pack: end;
  -ms-flex-pack: end;
  justify-content: flex-end;
  margin-right: 100px;
}
.topic-icon {
  margin-right: 40px;
}
.with-padding {
  margin-left: 8px;
  margin-right: 8px;
}
.status-text {
  font-size: 14px;
  white-space: nowrap;
  color: #c9cdd4;
  cursor: default;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
.btn-drafts {
  height: 32px;
  padding: 2px 16px;
  font-size: 14px;
  line-height: 22px;
  border: 1px solid #cc5656;
  border-radius: 2px;
  cursor: pointer;
  color: #cc5656;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  background-color: #fff;
  margin-right: 20px;
}
.with-padding {
  margin-left: 8px;
  margin-right: 8px;
}
.publish-btn {
  height: 32px;
  padding: 2px 16px;
  font-size: 14px;
  line-height: 22px;
  border: 1px solid #cc5656;
  border-radius: 2px;
  cursor: pointer;
  color: #fff;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  background-color: #cc5656;
}

.with-padding:last-child {
  padding-right: 0;
}
.navigator {
  position: relative;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
.toggle-btn {
  width: 2.667rem;
  height: 2.667rem;
  border-radius: 50%;
  background-color: #eee;
  background-position: 50%;
  background-size: cover;
  background-repeat: no-repeat;
  cursor: pointer;
}
.md-editor {
  width: 100%;
}
.md-editor ::v-deep .bytemd {
  height: calc(100vh - 5.334rem);
}
.md-editor ::v-deep .cm-s-default .cm-variable-2 {
  color: #000;
}
.button-group-right ::v-deep(.el-form-item__content) {
  display: flex;
  justify-content: flex-end;
}

.avatar-uploader ::v-deep .el-upload {
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: 0.2s;
}
.avatar-uploader ::v-deep .el-upload:hover {
  border-color: #409eff;
}
.el-icon.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 178px;
  height: 178px;
  text-align: center;
}
.upload-text {
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  color: #86909c;
  display: block;
  width: 100%;
}
::v-deep .el-form-item__label {
  width: 85px;
}
.plusdivider {
  width: 100%;
  height: 1px;
  border-bottom: 1px solid #dcdfe6;
  margin-bottom: 20px;
}

::v-deep .markdown-body img {
  max-width: 100%;
}

::v-deep .el-radio__inner {
  display: none;
}
::v-deep .el-radio {
  width: 98px;
  margin-right: 9px;
  margin-bottom: 10px;
}

/* 编辑器相关样式 */
::v-deep .markdown-body img {
  max-width: 100%;
}

::v-deep .el-radio__inner {
  display: none;
}
::v-deep .el-radio {
  width: 98px;
  margin-right: 9px;
  margin-bottom: 10px;
}
::v-deep .markdown-body {
  word-break:break-word;
  line-height:1.75;
  font-weight:400;
  font-size:16px;
  overflow-x:hidden;
  color:#252933
}
::v-deep .markdown-body h1,
::v-deep .markdown-body h2,
::v-deep .markdown-body h3,
::v-deep .markdown-body h4,
::v-deep .markdown-body h5,
::v-deep .markdown-body h6 {
  line-height:1.5;
  margin-top:35px;
  margin-bottom:10px;
  padding-bottom:5px
}
::v-deep .markdown-body h1 {
  font-size:24px;
  line-height:38px;
  margin-bottom:5px
}
::v-deep .markdown-body h2 {
  font-size:22px;
  line-height:34px;
  padding-bottom:12px;
  border-bottom:1px solid #ececec
}
::v-deep .markdown-body h3 {
  font-size:20px;
  line-height:28px
}
::v-deep .markdown-body h4 {
  font-size:18px;
  line-height:26px
}
::v-deep .markdown-body h5 {
  font-size:17px;
  line-height:24px
}
::v-deep .markdown-body h6 {
  font-size:16px;
  line-height:24px
}
::v-deep .markdown-body p {
  line-height:inherit;
  margin-top:22px;
  margin-bottom:22px
}
::v-deep .markdown-body img {
  max-width:100%
}
::v-deep .markdown-body hr {
  border:none;
  border-top:1px solid #ddd;
  margin-top:32px;
  margin-bottom:32px
}
::v-deep .markdown-body code {
  word-break:break-word;
  border-radius:2px;
  overflow-x:auto;
  background-color:#fff5f5;
  color:#ff502c;
  font-size:.87em;
  padding:.065em .4em
}
::v-deep .markdown-body code,
::v-deep .markdown-body pre {
  font-family:Menlo,Monaco,Consolas,Courier New,monospace
}
::v-deep .markdown-body pre {
  overflow:auto;
  position:relative;
  line-height:1.75
}
::v-deep .markdown-body pre>code {
  font-size:12px;
  padding:15px 12px;
  margin:0;
  word-break:normal;
  display:block;
  overflow-x:auto;
  color:#333;
  background:#f8f8f8
}
::v-deep .markdown-body a {
  text-decoration:none;
  color:#0269c8;
  border-bottom:1px solid #d1e9ff
}
::v-deep .markdown-body a:active,
::v-deep .markdown-body a:hover {
  color:#275b8c
}
::v-deep .markdown-body table {
  display:inline-block!important;
  font-size:12px;
  width:auto;
  max-width:100%;
  overflow:auto;
  border:1px solid #f6f6f6
}
::v-deep .markdown-body thead {
  background:#f6f6f6;
  color:#000;
  text-align:left
}
::v-deep .markdown-body tr:nth-child(2n) {
  background-color:#fcfcfc
}
::v-deep .markdown-body td,
::v-deep .markdown-body th {
  padding:12px 7px;
  line-height:24px
}
::v-deep .markdown-body td {
  min-width:120px
}
::v-deep .markdown-body blockquote {
  color:#666;
  padding:1px 23px;
  margin:22px 0;
  border-left:4px solid #cbcbcb;
  background-color:#f8f8f8
}
::v-deep .markdown-body blockquote:after {
  display:block;
  content:""
}
::v-deep .markdown-body blockquote>p {
  margin:10px 0
}
::v-deep .markdown-body ol,
::v-deep .markdown-body ul {
  padding-left:28px
}
::v-deep .markdown-body ol li,
::v-deep .markdown-body ul li {
  margin-bottom:0;
  list-style:inherit
}
::v-deep .markdown-body ol li .task-list-item,
::v-deep .markdown-body ul li .task-list-item {
  list-style:none
}
::v-deep .markdown-body ol li .task-list-item ol,
::v-deep .markdown-body ol li .task-list-item ul,
::v-deep .markdown-body ul li .task-list-item ol,
::v-deep .markdown-body ul li .task-list-item ul {
  margin-top:0
}
::v-deep .markdown-body ol ol,
::v-deep .markdown-body ol ul,
::v-deep .markdown-body ul ol,
::v-deep .markdown-body ul ul {
  margin-top:3px
}
::v-deep .markdown-body ol li {
  padding-left:6px
}
::v-deep .markdown-body .contains-task-list {
  padding-left:0
}
::v-deep .markdown-body .task-list-item {
  list-style:none
}
@media (max-width:720px) {
  ::v-deep .markdown-body h1 {
    font-size:24px
  }
  ::v-deep .markdown-body h2 {
    font-size:20px
  }
  ::v-deep .markdown-body h3 {
    font-size:18px
  }
}
</style>
